import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import ArticlesNav from "./components/ArticlesNav";
import { resolveApiError } from "../../utils/apiError";
import {
  deleteArticleCategory,
  getArticleCategories,
  normalizePaginated,
  splitArticleCategories,
} from "./articleApi";
import "./articles.scss";

const SORT_OPTIONS = [
  { value: "sort_order", label: "Sort order" },
  { value: "name", label: "Name" },
  { value: "created_at", label: "Created date" },
  { value: "updated_at", label: "Updated date" },
  { value: "status", label: "Status" },
];

const ArticleCategoryList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  const params = useMemo(
    () => ({
      sort_by: sortBy,
      sort_order: sortOrder,
      keyword: debouncedKeyword || undefined,
      status: status === "all" ? undefined : status,
    }),
    [sortBy, sortOrder, debouncedKeyword, status]
  );

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getArticleCategories({
        ...params,
        page: 1,
        per_page: 200,
      });
      const result = normalizePaginated(response, { per_page: 200 });
      const { parents, childrenByParent } = splitArticleCategories(result.items);
      const enriched = parents.map((parent) => ({
        ...parent,
        children: parent.children?.length
          ? parent.children
          : childrenByParent.get(Number(parent.id)) || [],
      }));

      const total = enriched.length;
      const lastPage = Math.max(1, Math.ceil(total / perPage) || 1);
      const currentPage = Math.min(Math.max(1, page), lastPage);
      const start = (currentPage - 1) * perPage;
      const pageRows = enriched.slice(start, start + perPage);

      setCategories(pageRows);
      setMeta({
        current_page: currentPage,
        last_page: lastPage,
        per_page: perPage,
        total,
        from: total ? start + 1 : 0,
        to: total ? start + pageRows.length : 0,
      });
      if (currentPage !== page) setPage(currentPage);
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [params, page, perPage, navigate]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!location.state?.notice) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const openDelete = (category) => {
    setDeleteError("");
    setPendingDelete(category);
  };

  const closeDelete = () => {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    if ((pendingDelete.children || []).length > 0) {
      setDeleteError("Cannot delete a category that still has subcategories.");
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteArticleCategory(pendingDelete.id);
      setPendingDelete(null);
      setNotice("Category deleted.");
      await loadCategories();
    } catch (err) {
      resolveApiError(err, setDeleteError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setKeyword("");
    setDebouncedKeyword("");
    setStatus("all");
    setSortBy("sort_order");
    setSortOrder("desc");
    setPerPage(50);
    setPage(1);
  };

  return (
    <div className="articles-page">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}

      <div className="articles-page__heading">
        <div>
          <h2>Categories</h2>
          <p>Organise articles into categories and optional subcategories.</p>
        </div>
        <button
          className="ui-button ui-button--primary"
          type="button"
          onClick={() => navigate("/admin/articles/categories/create")}
        >
          <Icon icon="ph:plus" height={14} /> New category
        </button>
      </div>

      <ArticlesNav />

      <section className="articles-page__card">
        <ModuleSearchControls value={keyword} onChange={setKeyword} placeholder="Search categories">
          <ModuleFilterMenu
            open={filterOpen}
            onToggle={() => {
              setFilterOpen((open) => !open);
              setSortOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setFilterOpen(false)}
            count={status === "all" ? 0 : 1}
            columns={1}
            size="narrow"
            ariaLabel="Category filters"
          >
            <fieldset>
              <legend>Status</legend>
              {[
                ["all", "All statuses"],
                ["1", "Active"],
                ["0", "Inactive"],
              ].map(([value, label]) => (
                <ModuleRadioOption
                  key={value}
                  name="article-category-status"
                  checked={status === value}
                  onChange={() => {
                    setStatus(value);
                    setPage(1);
                  }}
                  plain
                >
                  {label}
                </ModuleRadioOption>
              ))}
            </fieldset>
          </ModuleFilterMenu>
          <ModuleSortMenu
            open={sortOpen}
            onToggle={() => {
              setSortOpen((open) => !open);
              setFilterOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setSortOpen(false)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={SORT_OPTIONS}
            onChangeSortBy={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            onChangeSortOrder={(value) => {
              setSortOrder(value);
              setPage(1);
            }}
          />
          <ModulePerPageMenu
            open={perPageOpen}
            onToggle={() => {
              setPerPageOpen((open) => !open);
              setFilterOpen(false);
              setSortOpen(false);
            }}
            onClose={() => setPerPageOpen(false)}
            value={perPage}
            options={[10, 20, 50]}
            onChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
          <button className="ui-button" type="button" onClick={resetFilters}>
            <Icon icon="ph:arrow-clockwise" height={14} /> Reset
          </button>
        </ModuleSearchControls>

        <DataTable
          loading={loading}
          loadingMessage="Loading categories..."
          emptyMessage="No categories found."
          rows={categories}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (category) => <strong>{category.name}</strong>,
            },
            { key: "slug", header: "Slug" },
            { key: "status", header: "Status", render: (category) => (category.status ? "Active" : "Inactive") },
            { key: "sort_order", header: "Sort order", render: (category) => category.sort_order ?? 0 },
            {
              key: "actions",
              header: "Actions",
              render: (category) => (
                <DataTableActions>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/articles/categories/${category.id}/edit`)}
                  >
                    Edit
                  </button>
                  <button type="button" className="danger" onClick={() => openDelete(category)}>
                    Delete
                  </button>
                </DataTableActions>
              ),
            },
          ]}
        />
        <DataTableFooter meta={meta} loading={loading} onPageChange={setPage} />
      </section>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete category"
        message={
          pendingDelete
            ? `Delete “${pendingDelete.name}”? It cannot be deleted if it still has subcategories or is assigned to an article.`
            : ""
        }
        error={deleteError}
        confirming={deleting}
        onCancel={closeDelete}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ArticleCategoryList;
